import type { TonyteActivity } from '../types';
import { isEventActivity } from './activityKind';
import { sortActivitiesByStart } from './activityDates';
import { normalizeCityKey } from './cities';
import { isEventAvailableForDiscovery } from './eventAvailability';

// Events feed search: events, clubs/organisers/venues and tags. A search looks
// across every upcoming event in the selected city — not just the selected
// day or chips — because people search for something specific that may be on
// another day. Runs on the already-loaded feed pool (no extra reads).

export const FEED_SEARCH_MIN_LENGTH = 2;

// Case-, accent- and "#"-insensitive: "Bücher", "bucher" and "#BUCHER" match.
export function normalizeSearchText(value: string): string {
  return value
    .toLocaleLowerCase('de-DE')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[#@]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function searchTokens(query: string): string[] {
  const normalized = normalizeSearchText(query);
  return normalized.length >= FEED_SEARCH_MIN_LENGTH ? normalized.split(' ').filter(Boolean) : [];
}

const strings = (values: unknown[]): string[] =>
  values.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

function searchableText(activity: TonyteActivity): string {
  const extra = activity as TonyteActivity & { vibes?: unknown };
  return normalizeSearchText(strings([
    activity.title,
    activity.organizerName,
    activity.hostName,
    activity.venueName,
    activity.locationName,
    activity.address,
    activity.category,
    ...(activity.tags || []),
    ...(activity.hashtags || []),
    ...(activity.musicGenres || []),
    ...(Array.isArray(extra.vibes) ? extra.vibes : []),
  ]).join(' \u0001 '));
}

// Every word of the query must appear somewhere (title, club, venue, tags…),
// so "jazz neustadt" finds a jazz night at a Neustadt bar.
export function matchesFeedSearch(activity: TonyteActivity, tokens: string[]): boolean {
  if (!tokens.length) return true;
  const text = searchableText(activity);
  return tokens.every(token => text.includes(token));
}

export function searchFeedEvents(
  pool: TonyteActivity[],
  query: string,
  options: { cityKey?: string | null; now?: Date } = {},
): TonyteActivity[] {
  const tokens = searchTokens(query);
  if (!tokens.length) return [];
  const now = options.now ?? new Date();
  const cityKey = options.cityKey || null;
  return pool
    .filter(activity => isEventActivity(activity)
      && (!cityKey || normalizeCityKey(activity.cityKey || activity.city) === cityKey)
      && isEventAvailableForDiscovery(activity, now)
      && matchesFeedSearch(activity, tokens))
    .sort(sortActivitiesByStart);
}
